import React, { useState, useEffect } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Maximize2 } from 'lucide-react';
import { MOCK_GALLERY } from '../data';
import { imageUrl, srcSet } from '../lib/images';

export function GalleryGrid({ limit }) {
  const [items, setItems] = useState(limit ? MOCK_GALLERY.slice(0, limit) : MOCK_GALLERY);
  const [loading, setLoading] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);

  useEffect(() => {
    async function fetchGallery() {
      try {
        const response = await fetch('/_api/public/content/gallery_items');
        const data = await response.json();
        if (Array.isArray(data) && data.length > 0) {
          setItems(limit ? data.slice(0, limit) : data);
        }
      } catch (error) {
        console.error('Error fetching gallery:', error);
      }
    }
    fetchGallery();
  }, [limit]);

  if (loading) {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
        {[1, 2, 3, 4, 5, 6].map((i) => (
          <div key={i} className="aspect-square bg-emerald-50 animate-pulse rounded-3xl" />
        ))}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
      <AnimatePresence>
        {items.map((item, index) => (
          <motion.div
            key={item.id || index}
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: index * 0.1 }}
          >
            <Dialog.Root>
              <Dialog.Trigger asChild>
                <button
                  onClick={() => setSelectedImage(item)}
                  className="group relative aspect-square w-full overflow-hidden rounded-3xl bg-emerald-100 cursor-pointer"
                >
                  <img
                    src={imageUrl(item.image[0], 'width=500,quality=75,format=auto')}
                    srcSet={srcSet(item.image[0], [280, 500, 750])}
                    sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw"
                    loading="lazy"
                    decoding="async"
                    alt={item.title?.da || item.title || 'Galleri billede'}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-emerald-900/40 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                    <div className="bg-white/20 backdrop-blur-md p-4 rounded-full text-white transform translate-y-4 group-hover:translate-y-0 transition-transform duration-300">
                      <Maximize2 className="w-6 h-6" />
                    </div>
                  </div>
                  <div className="absolute bottom-0 left-0 right-0 p-6 bg-gradient-to-t from-emerald-950/80 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <p className="text-white font-medium text-sm">{item.title.da}</p>
                  </div>
                </button>
              </Dialog.Trigger>
              
              <Dialog.Portal>
                <Dialog.Overlay className="fixed inset-0 bg-emerald-950/90 backdrop-blur-sm z-[100] animate-in fade-in duration-300" />
                <Dialog.Content className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[95vw] max-w-5xl z-[101] animate-in zoom-in-95 duration-300 outline-none">
                  <div className="relative bg-emerald-900 rounded-3xl overflow-hidden shadow-2xl">
                    <img
                      src={imageUrl(item.image[0], 'width=1400,quality=80,format=auto')}
                      alt={item.title?.da || item.title || 'Galleri billede'}
                      className="w-full h-auto max-h-[85vh] object-contain mx-auto"
                      decoding="async"
                    />
                    <div className="absolute bottom-0 left-0 right-0 p-6 bg-emerald-950/80 backdrop-blur-md border-t border-emerald-800">
                      <Dialog.Title className="text-white font-bold text-lg">
                        {item.title.da}
                      </Dialog.Title>
                    </div>
                    <Dialog.Close asChild>
                      <button className="absolute top-4 right-4 p-2 bg-white/10 hover:bg-white/20 text-white rounded-full transition-colors backdrop-blur-md">
                        <X className="w-6 h-6" />
                      </button>
                    </Dialog.Close>
                  </div>
                </Dialog.Content>
              </Dialog.Portal>
            </Dialog.Root>
          </motion.div>
        ))}
      </AnimatePresence>
    </div>
  );
}
